import React from "react";
import { Button } from "@heroui/button";
import { Link } from "@heroui/link";
import { Loader2, Users, ChevronLeft, ChevronRight } from "lucide-react";

import { Customer } from "../../types/customers";

interface CustomersTableProps {
  customers: Customer[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (customer: Customer) => void;
  onView: (customerId: number) => void;
}

const CustomersTable: React.FC<CustomersTableProps> = ({
  customers,
  loading,
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onView,
}) => {

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount / 100);
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";

    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Loading state
  if (loading && customers.length === 0) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="inline-block w-8 h-8 animate-spin mb-4" />
        <p className="text-gray-500 dark:text-gray-400">Loading customers...</p>
      </div>
    );
  }

  // Empty state
  if (!loading && (!customers || customers.length === 0)) {
    return (
      <div className="p-8 text-center">
        <Users className="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
        <p className="text-xl font-semibold text-gray-600 dark:text-gray-300 mb-2">
          No customers found
        </p>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          Try adjusting your search or filter criteria.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                scope="col"
              >
                Customer
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                scope="col"
              >
                Contact
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                scope="col"
              >
                Orders
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                scope="col"
              >
                Total Spent
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                scope="col"
              >
                Last Order
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                scope="col"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {customers.map((customer) => (
              <tr
                key={customer.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-900"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                      <span className="text-gray-600 dark:text-gray-200 font-medium text-sm">
                        {customer.first_name.charAt(0)}
                        {customer.last_name ? customer.last_name.charAt(0) : ""}
                      </span>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {customer.first_name} {customer.last_name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Customer ID: {customer.id}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {customer.phone_number}
                  </div>
                  {customer.email && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {customer.email}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {customer.total_orders} orders
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {formatCurrency(customer.total_spent)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(customer.last_order_date)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex space-x-2 justify-end">
                    <Button
                      color="primary"
                      size="sm"
                      variant="light"
                      onPress={() => onView(customer.id)}
                    >
                      View
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      variant="light"
                      onPress={() => onEdit(customer)}
                    >
                      Edit
                    </Button>
                    <Button
                      as={Link}
                      color="primary"
                      href={`/orders?customer_id=${customer.id}`}
                      size="sm"
                      variant="light"
                    >
                      Orders
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
        <div className="flex-1 flex justify-between sm:hidden">
          <Button
            disabled={page <= 1}
            variant="light"
            onPress={() => onPageChange(page - 1)}
          >
            <ChevronLeft className="w-5 h-5 mr-2" /> Previous
          </Button>
          <Button
            disabled={page >= totalPages}
            variant="light"
            onPress={() => onPageChange(page + 1)}
          >
            Next <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Showing{" "}
              <span className="font-medium">
                {Math.min((page - 1) * pageSize + 1, total)}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(page * pageSize, total)}
              </span>{" "}
              of <span className="font-medium">{total}</span> results
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div>
              <label className="sr-only" htmlFor="rowsPerPage">
                Rows per page
              </label>
              <select
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                id="rowsPerPage"
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>

            <nav
              aria-label="Pagination"
              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
            >
              <Button
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium"
                disabled={page <= 1}
                variant="light"
                onPress={() => onPageChange(page - 1)}
              >
                <span className="sr-only">Previous</span>
                <ChevronLeft className="h-5 w-5" />
              </Button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Calculate which page numbers to show
                let pageNum;

                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }

                return (
                  <Button
                    key={pageNum}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium"
                    color={pageNum === page ? "primary" : "default"}
                    variant={pageNum === page ? "solid" : "light"}
                    onPress={() => onPageChange(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}

              <Button
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium"
                disabled={page >= totalPages}
                variant="light"
                onPress={() => onPageChange(page + 1)}
              >
                <span className="sr-only">Next</span>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomersTable;
